import { useCallback, useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import LinkIcon from "@/components/doc/right-sidebar/icons/Link.svg";
import UnlinkIcon from "@/components/doc/right-sidebar/icons/Unlink.svg";
import { Sidebar } from "@/components/sidebar";
import { type DocumentModel } from "@/models/DocumentModel";
import { twMerge } from "@/utils/twMerge";

export const LinkButton = observer(({ doc }: { doc: DocumentModel }) => {
  const t = useTranslations("sidebar");
  const { tiptap, editorState, isLinkEditMode, setIsLinkEditMode } = doc.editor;

  const { isLink, isCode, isExpression, isTextSelected } = editorState;

  const [isDisabled, setIsDisabled] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isCode) {
      setIsLinkEditMode(false);
      setIsDisabled(true);
      setIsActive(false);
    } else if (isLink) {
      setIsLinkEditMode(true);
      setIsDisabled(false);
      setIsActive(true);
    } else if (isTextSelected) {
      setIsLinkEditMode(false);
      setIsDisabled(false);
      setIsActive(false);
    } else {
      setIsLinkEditMode(false);
      setIsDisabled(true);
      setIsActive(false);
    }
    if (isExpression) {
      setIsDisabled(true);
    }
  }, [isLink, isCode, isTextSelected, isExpression, setIsDisabled, setIsLinkEditMode, setIsActive]);

  const handleClick = useCallback(() => {
    if (!isLinkEditMode) {
      setIsLinkEditMode(true, { focusOnRender: true });
    } else {
      tiptap?.commands.unsetLink();
    }
  }, [tiptap, isLinkEditMode, setIsLinkEditMode]);

  if (!tiptap) {
    return null;
  }

  return (
    <Sidebar.Button
      className={twMerge("font-mono")}
      icon={
        isHovered && isActive ? (
          <UnlinkIcon className={twMerge("[&>path]:stroke-icon-active")} />
        ) : (
          <LinkIcon className={twMerge(isActive && "[&>path]:stroke-icon-active")} />
        )
      }
      title={isActive ? t("textStyle.unlink") : t("textStyle.link")}
      onClick={handleClick}
      disabled={isDisabled}
      isActive={isActive}
      inverted
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      hintKey="link"
    />
  );
});
